// Referral codes — pure helpers for the BO pages (Phase 2 spec
// docs/specs/2026-10-13-phase2-referral-design.md §2.2, §3.1, §3.6, §5).
// Vue-free so jest can pin every rule without mounting a .vue file.
import { bkkLocalToIso, isoToBkkLocal } from "./marketing";

// ---------------------------------------------------------------------------
// Code format (§1.6 / §2.2) — ONE rule everywhere (www, member-fn, API, BO):
// remove all Unicode whitespace and "-", upper-case ASCII, ^[A-Z0-9]{4,16}$.
export const CODE_RE = /^[A-Z0-9]{4,16}$/;
export const PREFIX_RE = /^[A-Z0-9]{0,8}$/;
const STRIP_RE = /[\s\u0085-]/g;

export function cleanReferralInput(raw) {
	if (typeof raw !== "string") return "";
	return raw.replace(STRIP_RE, "").replace(/[a-z]+/g, (s) => s.toUpperCase());
}

export function normalizeReferralCode(raw) {
	const c = cleanReferralInput(raw);
	return CODE_RE.test(c) ? c : null;
}

// ---------------------------------------------------------------------------
// States (§3.1)
export const REFERRAL_STATES = ["active", "scheduled", "expired", "full", "disabled"];

/** el-tag type for a code state. */
export function referralStateTag(state) {
	return { active: "success", scheduled: "warning", expired: "info", full: "danger", disabled: "info" }[state] || "";
}

/** "3 / 100" or "3 / ∞". */
export function usageLabel(code) {
	const used = Number((code && code.used_count) || 0);
	const max = code && code.max_uses;
	return `${used} / ${max === null || max === undefined || max === "" ? "∞" : Number(max)}`;
}

/** True when the reward is a coupon whose template is no longer usable (red tag). */
export function rewardDead(code) {
	return !!(code && code.reward_type === "coupon" && code.reward_coupon && code.reward_coupon.active === false);
}

// ---------------------------------------------------------------------------
// List query (§3.1 GET /)
export const LIST_LIMIT = 20;

export function buildCodeListQuery(f) {
	const src = f || {};
	const params = { page: Math.max(1, Number(src.page) || 1), limit: LIST_LIMIT };
	if (REFERRAL_STATES.indexOf(src.state) !== -1) params.state = src.state;
	const q = String(src.q == null ? "" : src.q).trim();
	if (q) params.q = q;
	const owner = Number(src.owner_bo_user_id);
	if (Number.isInteger(owner) && owner > 0) params.owner_bo_user_id = owner;
	["team", "campaign", "batch_id"].forEach((k) => {
		const v = String(src[k] == null ? "" : src[k]).trim();
		if (v) params[k] = v;
	});
	return params;
}

// ---------------------------------------------------------------------------
// Landing (§3.1 validation, §5.3 item 6)
export const LANDING_MODES = ["home", "lp", "custom"];

/**
 * The path part (before ? / #) has a "." or ".." segment — also %2e-encoded —
 * or a segment that decodes to "/" or "\\". URL resolution collapses those:
 * "/..//evil.example" → "//evil.example" (open redirect via www /r/CODE).
 */
function hasDotOrEncodedSeparator(p) {
	const path = p.split(/[?#]/, 1)[0];
	const segs = path.split("/");
	for (let i = 0; i < segs.length; i++) {
		let d;
		try {
			d = decodeURIComponent(segs[i]);
		} catch (e) {
			return true; // malformed escape — refuse rather than guess
		}
		if (d === "." || d === ".." || d.indexOf("/") !== -1 || d.indexOf("\\") !== -1) return true;
	}
	return false;
}

/**
 * API rule (mirrors the server + www safeLandingPath): starts with a single
 * "/", ≤ 300, no scheme, no backslash, no control characters, no "." / ".." /
 * %2e segments, no encoded separators.
 */
export function landingProblem(path) {
	const p = String(path == null ? "" : path);
	if (!p.startsWith("/") || p.startsWith("//") || p.length > 300 || p.indexOf("\\") !== -1) return "invalid_landing";
	if (/^\/*[a-z][a-z0-9+.-]*:/i.test(p.slice(1))) return "invalid_landing";
	if (/[\u0000-\u001f\u007f]/.test(p)) return "invalid_landing";
	if (hasDotOrEncodedSeparator(p)) return "invalid_landing";
	return "";
}

/** landing_path → {mode, lp_slug, custom} for the radio group. */
export function landingFromPath(path) {
	const p = String(path || "/");
	if (p === "/") return { mode: "home", lp_slug: "", custom: "" };
	const m = /^\/lp\/([a-z0-9]+(?:-[a-z0-9]+)*)$/.exec(p);
	if (m) return { mode: "lp", lp_slug: m[1], custom: "" };
	return { mode: "custom", lp_slug: "", custom: p };
}

/** {mode, lp_slug, custom} → landing_path. */
export function landingPathOf(f) {
	if (!f || f.mode === "home") return "/";
	if (f.mode === "lp") return f.lp_slug ? `/lp/${f.lp_slug}` : "";
	return String(f.custom || "").trim();
}

// ---------------------------------------------------------------------------
// CodeInput (§3.1)
const collapse = (v) => String(v == null ? "" : v).trim().replace(/\s+/g, " ");
const trim = (v) => String(v == null ? "" : v).trim();
const LIMITS = { name: 120, team: 80, campaign: 120, note: 500 };

/** Integer > 0, or null for blank; NaN for anything else (the validator reports it). */
export function parseMaxUses(v) {
	if (v === null || v === undefined || String(v).trim() === "") return null;
	const s = String(v).trim();
	if (!/^\d+$/.test(s)) return NaN;
	const n = Number(s);
	return n > 0 ? n : NaN;
}

/**
 * Form → CodeInput. `form` = {name, owner_bo_user_id, team, campaign, note,
 * landing:{mode, lp_slug, custom}, reward_type, reward_coupon_template_id,
 * max_uses, starts_at, ends_at} — dates already RFC3339 (+07) or "" / null.
 */
export function codeInputPayload(form) {
	const f = form || {};
	const coupon = f.reward_type === "coupon";
	const owner = Number(f.owner_bo_user_id);
	const mu = parseMaxUses(f.max_uses);
	return {
		name: trim(f.name),
		owner_bo_user_id: Number.isInteger(owner) && owner > 0 ? owner : null,
		team: collapse(f.team),
		campaign: collapse(f.campaign),
		note: trim(f.note),
		landing_path: landingPathOf(f.landing),
		reward_type: coupon ? "coupon" : "none",
		reward_coupon_template_id: coupon && f.reward_coupon_template_id ? Number(f.reward_coupon_template_id) : null,
		max_uses: Number.isNaN(mu) ? null : mu,
		starts_at: f.starts_at || null,
		ends_at: f.ends_at || null,
	};
}

/**
 * Client-side mirror of the API validation → {field: rule}. Rules are the
 * contract's (§7.3): required|max_length|format|range|below_used, plus the
 * error codes invalid_landing / template_invalid. Empty object = OK.
 * opts: {mode: "create"|"edit"|"bulk", codeMode: "typed"|"generate", code, prefix, usedCount}
 */
export function validateCodeForm(form, opts) {
	const f = form || {};
	const o = opts || {};
	const e = {};
	if (o.mode === "create") {
		if (o.codeMode === "generate") {
			if (!PREFIX_RE.test(cleanReferralInput(o.prefix || ""))) e.prefix = "format";
		} else if (!normalizeReferralCode(o.code)) {
			e.code = cleanReferralInput(o.code) ? "format" : "required";
		}
	}
	Object.keys(LIMITS).forEach((k) => {
		if (Array.from(trim(f[k])).length > LIMITS[k]) e[k] = "max_length";
	});
	const lp = landingPathOf(f.landing);
	if (f.landing && f.landing.mode === "lp" && !lp) e.landing_path = "required";
	else if (landingProblem(lp)) e.landing_path = "invalid_landing";
	if (f.reward_type === "coupon" && !f.reward_coupon_template_id) e.reward_coupon_template_id = "required";
	const mu = parseMaxUses(f.max_uses);
	if (Number.isNaN(mu)) e.max_uses = "range";
	else if (mu !== null && o.mode === "edit" && Number(o.usedCount || 0) > mu) e.max_uses = "below_used";
	if (f.starts_at && f.ends_at && Date.parse(f.ends_at) <= Date.parse(f.starts_at)) e.ends_at = "range";
	return e;
}

/** invalid_body errors[{field, rule}] → {field: rule}. */
export function fieldErrors(err) {
	const d = err && err.response && err.response.data;
	const list = (d && (d.errors || (d.error && d.error.errors))) || [];
	const out = {};
	(Array.isArray(list) ? list : []).forEach((x) => {
		if (x && x.field && !out[x.field]) out[x.field] = x.rule || "format";
	});
	return out;
}

// ---------------------------------------------------------------------------
// Bulk (§3.1 POST /bulk, §5.4)
export const BULK_MAX = 200;

/** {ok, payload} or {ok:false, errors:{field: rule}}. */
export function bulkPayload(form, codeForm) {
	const f = form || {};
	const errors = {};
	const prefix = cleanReferralInput(f.prefix || "");
	if (!PREFIX_RE.test(prefix)) errors.prefix = "format";
	const payload = { mode: f.mode === "count" ? "count" : "per_owner", prefix };
	if (payload.mode === "per_owner") {
		const ids = Array.from(new Set((f.owner_bo_user_ids || []).map(Number).filter((n) => Number.isInteger(n) && n > 0)));
		if (ids.length === 0) errors.owner_bo_user_ids = "required";
		else if (ids.length > BULK_MAX) errors.owner_bo_user_ids = "too_many";
		payload.owner_bo_user_ids = ids;
	} else {
		const s = String(f.count == null ? "" : f.count).trim();
		const n = /^\d+$/.test(s) ? Number(s) : NaN;
		if (!(n >= 1 && n <= BULK_MAX)) errors.count = n > BULK_MAX ? "too_many" : "range";
		payload.count = n;
	}
	const shared = codeInputPayload(codeForm);
	const sharedErr = validateCodeForm(codeForm, { mode: "bulk" });
	Object.assign(errors, sharedErr);
	delete shared.owner_bo_user_id; // per_owner sets it per code; count leaves it empty
	if (!shared.name) delete shared.name; // absent → the API's default name (§3.1)
	Object.assign(payload, shared);
	return Object.keys(errors).length ? { ok: false, errors, payload } : { ok: true, payload };
}

const csvCell = (v) => {
	const s = v === null || v === undefined ? "" : String(v);
	// Defuse spreadsheet formulas (=, +, -, @) the way the API's CSVs do.
	const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
	return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

/** Client-side CSV of a bulk run: code, name, owner, link (UTF-8 BOM, CRLF). */
export function bulkCsv(codes, headers) {
	const h = headers || ["code", "name", "owner", "link"];
	const lines = [h.map(csvCell).join(",")];
	(codes || []).forEach((c) => lines.push([c.code, c.name, c.owner_name, c.link].map(csvCell).join(",")));
	return "﻿" + lines.join("\r\n") + "\r\n";
}

// ---------------------------------------------------------------------------
// Coupon-template picker label (§5.3 item 7)
export function templateLabel(t, tr) {
	if (!t) return "";
	const x = tr || ((k, p) => `${k}${p ? JSON.stringify(p) : ""}`);
	const num = (n) => Number(n || 0).toLocaleString("th-TH", { maximumFractionDigits: 2 });
	const discount = t.discount_type === "percent" || t.discount_type === "percentage"
		? `${num(t.discount_value)}%`
		: `฿${num(t.discount_value)}`;
	const parts = [t.name_th || t.name_en || t.code, x("marketing.referral.tpl.discount", { v: discount })];
	if (t.expiry_days) parts.push(x("marketing.referral.tpl.expiry", { n: t.expiry_days }));
	parts.push(t.quota_left === null || t.quota_left === undefined ? x("marketing.referral.tpl.unlimited") : x("marketing.referral.tpl.left", { n: t.quota_left }));
	return parts.join(" · ");
}

// ---------------------------------------------------------------------------
// Report (§3.6 / §5.5)
export const REFERRAL_GROUPS = ["code", "owner", "team", "campaign"];
export const REFERRAL_COHORTS = ["all", "signed_up_in_range"];
export const MEMBER_SORTS = ["signed_up_at", "orders", "revenue_completed"];
export const NONE = "(none)";
const DAY = 86400000;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** {ok, params} for /summary, /members, both CSVs — or {ok:false, error:"invalid_range"}. */
export function buildReferralReportQuery(f) {
	const src = f || {};
	const r = src.range;
	if (!Array.isArray(r) || r.length !== 2 || !DATE_RE.test(r[0] || "") || !DATE_RE.test(r[1] || "")) {
		return { ok: false, error: "invalid_range" };
	}
	const days = (Date.parse(r[1]) - Date.parse(r[0])) / DAY + 1;
	if (!(days >= 1 && days <= 366)) return { ok: false, error: "invalid_range" };
	const params = {
		from: r[0],
		to: r[1],
		cohort: REFERRAL_COHORTS.indexOf(src.cohort) !== -1 ? src.cohort : "all",
		group_by: REFERRAL_GROUPS.indexOf(src.group_by) !== -1 ? src.group_by : "code",
	};
	const codeId = Number(src.code_id);
	if (Number.isInteger(codeId) && codeId > 0) params.code_id = codeId;
	if (src.owner_bo_user_id === NONE) params.owner_bo_user_id = NONE;
	else {
		const owner = Number(src.owner_bo_user_id);
		if (Number.isInteger(owner) && owner > 0) params.owner_bo_user_id = owner;
	}
	["team", "campaign", "q"].forEach((k) => {
		const v = String(src[k] == null ? "" : src[k]).trim();
		if (v) params[k] = v;
	});
	if (src.page) params.page = Math.max(1, Number(src.page) || 1);
	if (src.limit) params.limit = Math.min(100, Math.max(1, Number(src.limit) || 20));
	if (MEMBER_SORTS.indexOf(src.sort) !== -1) params.sort = src.sort;
	return { ok: true, params };
}

/** Key columns shown for a grouping. */
export function referralKeyColumns(groupBy) {
	switch (groupBy) {
		case "owner":
			return ["owner_name"];
		case "team":
			return ["team"];
		case "campaign":
			return ["campaign"];
		default:
			return ["code", "name", "owner_name", "team", "campaign"];
	}
}

/** A row key's value for a column; "" when empty (the view prints the "(ไม่ระบุ)" label). */
export function referralKeyValue(key, col) {
	const v = key && key[col];
	return v === undefined || v === null ? "" : String(v);
}

/**
 * A summary row → the exact-match filters of its drill-down (/members):
 * code → code_id; owner → owner_bo_user_id ("(none)" for NULL);
 * team / campaign → value ("(none)" for empty).
 */
export function drillFilters(key, groupBy) {
	const k = key || {};
	switch (groupBy) {
		case "owner":
			return { owner_bo_user_id: k.owner_bo_user_id ? Number(k.owner_bo_user_id) : NONE };
		case "team":
			return { team: k.team ? String(k.team) : NONE };
		case "campaign":
			return { campaign: k.campaign ? String(k.campaign) : NONE };
		default:
			return k.code_id ? { code_id: Number(k.code_id) } : {};
	}
}

/** Cards (§5.5): สมัครใหม่, ได้รับคูปอง, สมาชิกที่สั่งซื้อ (+%), ออเดอร์, ยอดสั่งซื้อ, ยอดสำเร็จ, ยอดยกเลิก/ตีกลับ. */
export function referralCards(totals) {
	const t = totals || {};
	const n = (v) => Number(v) || 0;
	return {
		signups: n(t.signups),
		signups_rewarded: n(t.signups_rewarded),
		members_ordered: n(t.members_ordered),
		conversion_pct: n(t.conversion_pct),
		orders: n(t.orders),
		revenue_placed: n(t.revenue_placed),
		revenue_completed: n(t.revenue_completed),
		revenue_cancelled_returned: n(t.revenue_cancelled_returned),
	};
}

/**
 * The phone as the drill-down shows it. The API already masks (§3.6,
 * "0XX-xxx-NNNN"); this is the PDPA backstop — anything that still looks like a
 * full number is masked here, so a server regression can never print one.
 */
export function maskedPhone(v) {
	const s = String(v == null ? "" : v).trim();
	if (!s) return "-";
	if (/x/i.test(s)) return s;
	const d = s.replace(/\D/g, "");
	if (d.length >= 9) return `${d.slice(0, 3)}-xxx-${d.slice(-4)}`;
	return "xxx";
}

export const REWARD_STATUSES = ["none", "pending", "granted", "failed"];
export const REWARD_ERRORS = ["template_inactive", "template_missing", "template_expired", "quota_exhausted", "error"];

export function rewardStatusTag(status) {
	return { none: "info", pending: "warning", granted: "success", failed: "danger" }[status] || "info";
}

/** ให้คูปองอีกครั้ง is offered for failed grants only (§5.5); pending = awaiting phone verification. */
export function canRetryGrant(row) {
	return !!row && row.reward_status === "failed";
}

// ---------------------------------------------------------------------------
// Dialog form ⇄ Code (§5.3). Dates are edited as a Bangkok wall clock
// ("yyyy-MM-ddTHH:mm:ss", the phase 1 picker convention) and sent as +07:00.

export function emptyCodeForm() {
	return {
		name: "",
		owner_bo_user_id: null,
		team: "",
		campaign: "",
		note: "",
		landing: { mode: "home", lp_slug: "", custom: "" },
		reward_type: "none",
		reward_coupon_template_id: null,
		max_uses: "",
		starts_local: "",
		ends_local: "",
	};
}

export function formFromCode(c) {
	const code = c || {};
	return {
		name: code.name || "",
		owner_bo_user_id: code.owner_bo_user_id || null,
		team: code.team || "",
		campaign: code.campaign || "",
		note: code.note || "",
		landing: landingFromPath(code.landing_path),
		reward_type: code.reward_type === "coupon" ? "coupon" : "none",
		reward_coupon_template_id: code.reward_coupon_template_id || null,
		max_uses: code.max_uses === null || code.max_uses === undefined ? "" : String(code.max_uses),
		starts_local: isoToBkkLocal(code.starts_at),
		ends_local: isoToBkkLocal(code.ends_at),
	};
}

/** The dialog form with its dates converted → the shape codeInputPayload / validateCodeForm read. */
export function withIsoDates(form) {
	const f = form || {};
	return { ...f, starts_at: bkkLocalToIso(f.starts_local), ends_at: bkkLocalToIso(f.ends_local) };
}

export const CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789"; // no 0 O 1 I (§2.2)

/** Preview of a generated code (§2.2): prefix + max(6, 8 − len(prefix)) random chars. The server re-checks uniqueness. */
export function randomReferralCode(prefix, rnd = Math.random) {
	const p = cleanReferralInput(prefix || "").slice(0, 8);
	const n = Math.max(6, 8 - p.length);
	let s = p;
	for (let i = 0; i < n; i++) s += CODE_ALPHABET[Math.floor(rnd() * CODE_ALPHABET.length) % CODE_ALPHABET.length];
	return s;
}
